import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { checkUserByEmail, hasActiveSubscription } from '../utils/checkUser';
import { submitPurchase, stashSignupPassword } from '../utils/purchase';
import { PLAN_CONFIG } from '../constants/plans';
import { CheckoutGeoipInfo } from '../components/CheckoutGeoipInfo';
import { CheckoutPaymentForm } from '../components/CheckoutPaymentForm';
import { checkoutV2Strings } from '../constants/checkoutV2Locales';
import { getCheckoutHostname } from '../utils/getHostname';
import { CheckoutTrustBadges } from '../components/CheckoutTrustBadges';
import { identifyPosthogPerson } from '../utils/identifyPosthogPerson';
import './Checkout.css';

function Checkout() {
  const navigate = useNavigate();
  const str = checkoutV2Strings('en');
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [isLoadingCheckout, setIsLoadingCheckout] = useState(false);
  const [isPaying, setIsPaying] = useState(false);
  const [paymentError, setPaymentError] = useState(null);
  const [isActiveSubscriptionError, setIsActiveSubscriptionError] = useState(false);
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [selectedPlanKey, setSelectedPlanKey] = useState('vpn_30_day');

  useDocumentTitle(str.documentTitle);

  const planConfig = PLAN_CONFIG;

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const plan = params.get('plan');
    if (plan && planConfig[plan]) {
      setSelectedPlanKey(plan);
    }
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const affid = params.get('affid');
    const clickid = params.get('clickid');
    if (affid != null && affid !== '') {
      localStorage.setItem('vpnnext_affid', affid);
    }
    if (clickid != null && clickid !== '') {
      localStorage.setItem('vpnnext_clickid', clickid);
    }
  }, []);

  const generatePassword = () => {
    if (window.crypto?.randomUUID) {
      return window.crypto.randomUUID();
    }
    return `vpn-${Math.random().toString(36).slice(2, 10)}`;
  };

  const amount = planConfig[selectedPlanKey] || planConfig.vpn_30_day;

  const handleProceedToPayment = async (e) => {
    e.preventDefault();
    const emailInput = e.target.querySelector('input[type="email"]');
    const email = emailInput?.value?.trim();
    identifyPosthogPerson(email, { immediate: true });

    if (!email) {
      setPaymentError(str.pleaseEnterEmail);
      return;
    }

    setPaymentError(null);
    setIsActiveSubscriptionError(false);
    setIsLoadingCheckout(true);

    try {
      const existing = await checkUserByEmail(email);
      if (existing.found && hasActiveSubscription(existing.user)) {
        setIsActiveSubscriptionError(true);
        setPaymentError(str.activeSubscriptionFallback);
        return;
      }

      setFormData({ email, password: generatePassword() });
      setShowPaymentForm(true);
    } catch (err) {
      setPaymentError(err.message || str.initCheckoutFailed);
    } finally {
      setIsLoadingCheckout(false);
    }
  };

  const handlePay = async (payment) => {
    setPaymentError(null);
    setIsPaying(true);
    try {
      const result = await submitPurchase({
        payment,
        email: formData.email,
        plan: selectedPlanKey,
      });
      if (!result.ok) {
        setPaymentError(result.error || str.paymentFailed);
        return;
      }
      if (!result.id) {
        setPaymentError('Payment succeeded but no transaction id was returned.');
        return;
      }
      stashSignupPassword(formData.password);
      const params = new URLSearchParams();
      params.set('transaction_id', result.id);
      navigate(`/success?${params.toString()}`);
    } catch (err) {
      setPaymentError(err.message || str.paymentFailed);
    } finally {
      setIsPaying(false);
    }
  };

  return (
    <div className="checkout-page checkout-page--v2">
      <div className="checkout-card">
        <div className="checkout-geoip-slot">
          <CheckoutGeoipInfo />
        </div>
        <h1 className="checkout-header-title">
          {str.completeRegistration}
        </h1>
        <div className="checkout-columns">
          <div className="checkout-left">
            <div className="checkout-v2-left-content">
              <div className="checkout-v2-logo-container checkout-logo-row">
                <img
                  src="/images/logo.svg"
                  alt={str.vpnLogoAlt}
                  className="checkout-logo"
                />
                <div>
                  <div className="checkout-brand-name">
                    {getCheckoutHostname()}
                  </div>
                  <div className="checkout-plan-desc">
                    {amount.description}
                  </div>
                </div>
              </div>
              <div className="checkout-v2-benefits-container checkout-benefits-wrap">
                <div className="checkout-benefits-inner">
                  <h2 className="checkout-benefits-title">
                    {str.useVpnUnlock}
                  </h2>
                  <ul className="checkout-benefits-list">
                    <li>{str.streamingServices}</li>
                    <li>{str.onlineContent}</li>
                    <li>{str.socialMedia}</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
          <div className="checkout-right">
            {showPaymentForm ? (
              <CheckoutPaymentForm
                email={formData.email}
                accountLabel={str.accountLabel}
                copy={str}
                hideRegion
                error={paymentError}
                disabled={isPaying}
                onPay={handlePay}
              />
            ) : (
              <form className="checkout-form" onSubmit={handleProceedToPayment}>
                <label className="checkout-label">
                  {str.emailLabel}{' '}
                  <span className="checkout-required">*</span>
                </label>
                <input
                  type="email"
                  placeholder={str.emailPlaceholder}
                  required
                  className="checkout-input"
                  onChange={(e) => identifyPosthogPerson(e.target.value)}
                  onBlur={(e) => identifyPosthogPerson(e.target.value, { immediate: true })}
                />

                <h3 className="checkout-section-title" style={{ marginTop: '20px' }}>
                  {str.orderSummary}
                </h3>
                <div className="checkout-order-summary" style={{ marginBottom: '32px' }}>
                  <table className="checkout-order-table">
                    <tbody>
                      <tr>
                        <td>{str.plan}</td>
                        <td>{amount.label}</td>
                      </tr>
                      <tr>
                        <td>{str.billing}</td>
                        <td>{str.billingOnce}</td>
                      </tr>
                    </tbody>
                  </table>
                  <div className="checkout-order-total">
                    <span>{str.totalDueToday}</span>
                    <span>${amount.display}</span>
                  </div>
                </div>

                <button
                  type="submit"
                  className="checkout-btn-primary"
                  disabled={isLoadingCheckout}
                >
                  {isLoadingCheckout
                    ? str.loading
                    : str.continueSecurePayment}
                </button>
                {paymentError && (
                  <div
                    className={`checkout-error${isActiveSubscriptionError ? ' checkout-error--warning' : ''}`}
                    style={{ marginTop: '12px' }}
                  >
                    {paymentError}
                    {isActiveSubscriptionError && (
                      <div style={{ marginTop: '10px' }}>
                        <Link to="/login" className="checkout-link">
                          {str.logInInstead}
                        </Link>
                      </div>
                    )}
                  </div>
                )}
              </form>
            )}

            <div className="checkout-legal">
              {str.agreePrefix}<a href="/tos" className="checkout-link">{str.termsOfService}</a> {str.andWord} <a href="/privacy-policy" className="checkout-link">{str.privacyPolicy}</a>{str.agreeSuffix}
            </div>
            <CheckoutTrustBadges
              labels={{
                secure: str.secure,
                encrypted: str.encrypted,
                pciDss: str.pciDss,
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default Checkout;
